import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { recordManualDistribution } from './stakeholderApi'

interface Option {
  id: string
  label: string
}

interface ManualDistributionModalProps {
  open: boolean
  periodId: string | null
  stakeholders: readonly Option[]
  onClose: () => void
}

interface FormValues {
  stakeholderId: string
  amount: Money | null
  note: string
}

/** Moliyachi tasdiqlangan summani o'zi kiritadi — yozuv darhol APPROVED bo'ladi. */
export function ManualDistributionModal({ open, periodId, stakeholders, onClose }: ManualDistributionModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    if (open) form.resetFields()
  }, [open, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      if (!periodId) throw new Error('period')
      return recordManualDistribution({
        stakeholderId: values.stakeholderId,
        periodId,
        approvedMinor: values.amount?.amount ?? 0,
        note: values.note.trim(),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('stakeholder.manual.title')}
      okText={t('stakeholder.manual.submit')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('stakeholder.manual.note')} />
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="stakeholderId"
          label={t('stakeholder.distribution.stakeholder')}
          rules={[{ required: true, message: t('stakeholder.validation.stakeholder') }]}
        >
          <Select showSearch optionFilterProp="label" options={stakeholders.map((s) => ({ value: s.id, label: s.label }))} />
        </Form.Item>
        <Form.Item
          name="amount"
          label={t('stakeholder.approve.amount')}
          rules={[
            {
              validator: (_, value: Money | null) =>
                value && value.amount > 0 ? Promise.resolve() : Promise.reject(new Error(t('stakeholder.validation.amount'))),
            },
          ]}
        >
          <MoneyInput minMinor={1} />
        </Form.Item>
        <Form.Item
          name="note"
          label={t('stakeholder.approve.basis')}
          rules={[{ required: true, whitespace: true, message: t('stakeholder.validation.basis') }]}
        >
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
